import { cn } from '@/lib/utils/cn';
import { formatCurrency } from '@/lib/currency';

interface SubscriptionPriceCardProps {
    id: string;
    name: string;
    selected: boolean;
    price: number;
    originalPrice?: number;
    currency: string;
    interval: 'month' | 'year';
    trialDays: number;
    hasPastSubscription: boolean;
    savingsBadge?: string;
    onSelect: () => void;
    disabled?: boolean;
}

export const SubscriptionPriceCard = ({
    id,
    name,
    selected,
    price,
    originalPrice,
    currency,
    interval,
    trialDays,
    hasPastSubscription,
    savingsBadge,
    onSelect,
    disabled = false,
}: SubscriptionPriceCardProps) => {
    const intervalLabel = interval === 'month' ? 'mo' : 'yr';

    return (
        <label
            htmlFor={id}
            className={cn(
                'relative flex cursor-pointer items-center rounded-md border-2 p-5 transition duration-200 hover:scale-105 active:scale-95',
                'to-brand-dimmed from-base-fg-white bg-gradient-to-r',
                selected ? 'border-brand-main' : 'border-neutral-disabled',
                disabled && 'cursor-not-allowed opacity-50'
            )}
        >
            <div className='flex w-full items-center gap-4'>
                {/* Custom Radio Button */}
                <input
                    id={id}
                    type='radio'
                    name='subscriptionOptions'
                    checked={selected}
                    onChange={onSelect}
                    disabled={disabled}
                    className='sr-only'
                />
                <div
                    className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded-full border-2 md:size-5',
                        selected ? 'border-brand-main' : 'border-neutral-disabled'
                    )}
                >
                    <div
                        className={cn(
                            'bg-brand-main size-2 rounded-full transition-transform duration-200 md:size-2.5',
                            selected ? 'scale-100' : 'scale-0'
                        )}
                    />
                </div>

                {/* Pricing Container */}
                <div className='flex flex-1 flex-col'>
                    {/* Savings Badge - positioned at top right */}
                    {savingsBadge && (
                        <div className='flex justify-end'>
                            <div className='from-info-main to-brand-main -mt-8 flex h-6 items-center rounded-full bg-gradient-to-r px-2 md:-mt-9'>
                                <span className='text-caption-2-bold text-inverse block font-semibold'>
                                    {savingsBadge}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Plan Name */}
                    <p className='text-body-lg text-dark font-bold'>{name}</p>

                    {/* Pricing */}
                    <p className='text-label-sm-regular text-dark font-medium'>
                        {currency.toLowerCase() === 'usd' && !hasPastSubscription && (
                            <span>$0 for {trialDays} days, then </span>
                        )}
                        <span className='font-bold'>{formatCurrency(price, currency, 'en-US', 0)}</span>
                        {originalPrice && originalPrice !== price && (
                            <span className='text-gray line-through'>
                                {formatCurrency(originalPrice, currency, 'en-US', 0)}
                            </span>
                        )}
                        <span>/{intervalLabel}</span>
                    </p>
                </div>
            </div>
        </label>
    );
};
